import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './WordGroupPage.css';
import OnScreenKeyboard from './OnScreenKeyboard';
import { useLanguage } from '../contexts/LanguageContext';
import { getKeyboardLangCode } from '../utils/keyboardUtils';
import { isRTL as isRTLanguage } from '../utils/rtlUtils';
import { isTextSafe } from '../utils/safeForAds';

const DEMO_MODULE = {
  id: 'demo',
  from: 'English',
  to: 'Spanish',
  topic: 'Travel',
  dialect: 'Mexican',
  word_groups: [
    {
      id: 'demo_group_1', // Add ID for demo group
      name: 'Travel',
      words: [
        { English: 'to travel', Spanish: 'viajar' },
        { English: 'airport', Spanish: 'el aeropuerto' },
        { English: 'airplane ticket', Spanish: 'el boleto de avión' },
        { English: 'passport', Spanish: 'el pasaporte' },
        { English: 'hotel', Spanish: 'el hotel' },
        { English: 'reservation', Spanish: 'la reservación' },
        { English: 'to check in', Spanish: 'registrarse' },
        { English: 'to check out', Spanish: 'retirarse' },
        { English: 'bus', Spanish: 'el camión' },
        { English: 'taxi', Spanish: 'el taxi' },
        { English: 'subway, metro', Spanish: 'el metro' },
        { English: 'where is...?', Spanish: '¿Dónde está...?' },
        { English: 'how much does it cost?', Spanish: '¿Cuánto cuesta?' },
        { English: 'the bill, the check', Spanish: 'la cuenta' },
        { English: 'please', Spanish: 'por favor' },
        { English: 'thank you', Spanish: 'gracias' },
        { English: 'I am lost', Spanish: 'Estoy perdido/a' },
        { English: 'help', Spanish: '¡Auxilio!, ¡Ayuda!' },
        { English: 'I need a doctor', Spanish: 'Necesito un doctor' },
        { English: 'What\'s up?', Spanish: '¿Qué onda?' },
        { English: 'cool, awesome', Spanish: 'chido' },
        { English: 'No way!, You\'re kidding!', Spanish: '¡No manches!' },
        { English: 'Watch out!, Be careful!', Spanish: '¡Aguas!' },
        { English: 'right on!, go for it!, wow!', Spanish: '¡Órale!' },
        { English: 'dude, guy', Spanish: 'güey' },
      ],
    },
  ],
};

function DemoWordGroupPage() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [group, setGroup] = useState(() => ({ ...DEMO_MODULE.word_groups[0], words: [...DEMO_MODULE.word_groups[0].words] }));
  const [isEditing, setIsEditing] = useState(false);
  const [editedWords, setEditedWords] = useState([...group.words]);
  const [showAddWords, setShowAddWords] = useState(false);
  const wordToInputRefs = useRef([]);
  const keyboardOverlayRef = useRef(null);
  const [focusedInputIdx, setFocusedInputIdx] = useState(null);
  const [focusedLang, setFocusedLang] = useState(null);
  const [keyboardArmedLang, setKeyboardArmedLang] = useState(null);
  const [keyboardActiveIdx, setKeyboardActiveIdx] = useState(null);

  // Edit/Save logic
  const handleEditWords = () => {
    setIsEditing(true);
    setEditedWords([...group.words]);
  };
  const handleSaveWords = () => {
    // Check all edited words for inappropriate content
    for (let i = 0; i < editedWords.length; i++) {
      const word = editedWords[i];
      if (!isTextSafe(word[DEMO_MODULE.from]) || !isTextSafe(word[DEMO_MODULE.to])) {
        // For demo, we'll just skip inappropriate words instead of showing an error
        continue;
      }
    }
    
    // Filter out inappropriate words
    const safeWords = editedWords.filter(word => 
      isTextSafe(word[DEMO_MODULE.from]) && isTextSafe(word[DEMO_MODULE.to])
    );
    
    setGroup(g => ({ ...g, words: [...safeWords] }));
    setIsEditing(false);
    setShowAddWords(false);
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedWords(group.words);
    setShowAddWords(false);
  };
  const handleWordChange = (index, field, value) => {
    const updatedWords = [...editedWords];
    // Limit toLang words to 150 characters
    if (field === DEMO_MODULE.to) {
      updatedWords[index] = {
        ...updatedWords[index],
        [field]: value.substring(0, 150)
      };
    } else {
      updatedWords[index] = {
        ...updatedWords[index],
        [field]: value
      };
    }
    setEditedWords(updatedWords);
  };
  const handleDeleteWord = (index) => {
    const updatedWords = editedWords.filter((_, i) => i !== index);
    setEditedWords(updatedWords);
  };
  // Add More Words (manual only)
  const handleWordsAdded = (newWords) => {
    setGroup(g => ({ ...g, words: [...g.words, ...newWords] }));
    setEditedWords(ew => [...ew, ...newWords]);
    setShowAddWords(false);
  };

  // Keyboard overlay logic
  React.useEffect(() => {
    if (!focusedLang) return;
    function handleClick(e) {
      const input = wordToInputRefs.current[focusedInputIdx];
      const overlay = keyboardOverlayRef.current;
      if (
        input && !input.contains(e.target) &&
        overlay && !overlay.contains(e.target)
      ) {
        setFocusedLang(null);
        setFocusedInputIdx(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [focusedLang, focusedInputIdx]);

  // Add More Words panel (manual and AI tab, AI is alert-only)
  function AddMoreWordsPanel({ onWordsAdded }) {
    const [activeTab, setActiveTab] = useState('manual');
    // Manual tab state
    const [pairs, setPairs] = useState([{ left: '', right: '' }]);
    const [keyboardArmedLang, setKeyboardArmedLang] = useState(null);
    const [keyboardActiveIdx, setKeyboardActiveIdx] = useState(null);
    const [focusedInputIdx, setFocusedInputIdx] = useState(null);
    const rightInputRefs = useRef([]);
    const isTargetRTL = isRTLanguage(getKeyboardLangCode(DEMO_MODULE.to));
    const handlePairChange = (index, side, value) => {
      const newPairs = [...pairs];
      // Limit toLang words (right side) to 150 characters
      if (side === 'right') {
        newPairs[index][side] = value.substring(0, 150);
      } else {
        newPairs[index][side] = value;
      }
      setPairs(newPairs);
    };
    const handleAddPair = () => setPairs([...pairs, { left: '', right: '' }]);
    const handleRemovePair = (index) => setPairs(pairs.filter((_, i) => i !== index));
    const handleManualAdd = () => {
      const filledPairs = pairs.filter(p => p.left.trim() && p.right.trim());
      if (filledPairs.length === 0) return;
      
      // Filter out inappropriate content
      const safePairs = filledPairs.filter(pair => 
        isTextSafe(pair.left) && isTextSafe(pair.right)
      );
      
      if (safePairs.length === 0) return;
      
      const newWords = safePairs.map(p => ({ [DEMO_MODULE.from]: p.left, [DEMO_MODULE.to]: p.right }));
      onWordsAdded(newWords);
      setPairs([{ left: '', right: '' }]);
    };
    // Add state at the top of AddMoreWordsPanel:
    const [aiWordCount, setAiWordCount] = useState(25);
    const [showAICreateMsg, setShowAICreateMsg] = useState(false);
    return (
      <div className="add-more-words-container">
        <div className="add-words-tabs">
          <button
            className={`tab-button${activeTab === 'manual' ? ' active' : ''}`}
            onClick={() => setActiveTab('manual')}
          >
            {t('addWordsManually')}
          </button>
          <button
            className={`tab-button${activeTab === 'ai' ? ' active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            {t('generateWithAI')}
          </button>
        </div>
        {activeTab === 'manual' && (
          <div className="manual-add-section">
            <div className="word-pairs-section">
              <div className="language-direction-header">
                <span>{t(DEMO_MODULE.from)}</span>
                <span>{t(DEMO_MODULE.to)}</span>
              </div>
              {pairs.map((pair, index) => (
                <div key={index} className="word-pair-row">
                  <input
                    type="text"
                    placeholder={`${t(DEMO_MODULE.from)} ${t('word')}`}
                    value={pair.left}
                    onChange={e => handlePairChange(index, 'left', e.target.value)}
                    style={{ direction: 'ltr', textAlign: 'left' }}
                    className={isTargetRTL ? 'placeholder-rtl' : 'placeholder-ltr'}
                  />
                  <span className="arrow-separator">{isTargetRTL ? '←' : '→'}</span>
                  <div className="input-with-keyboard-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder={`${t(DEMO_MODULE.to)} ${t('translation')}`}
                      value={pair.right}
                      onChange={e => handlePairChange(index, 'right', e.target.value)}
                      style={{
                        direction: isTargetRTL ? 'rtl' : 'ltr',
                        textAlign: isTargetRTL ? 'right' : 'left',
                        paddingRight: !isTargetRTL ? '2.2rem' : undefined,
                        paddingLeft: isTargetRTL ? '2.2rem' : undefined
                      }}
                      className={isTargetRTL ? 'placeholder-rtl' : 'placeholder-ltr'}
                      ref={el => rightInputRefs.current[index] = el}
                      onFocus={() => {
                        setFocusedInputIdx(index);
                        if (keyboardArmedLang === getKeyboardLangCode(DEMO_MODULE.to)) {
                          setKeyboardActiveIdx(index);
                        }
                      }}
                      onBlur={e => {
                        setFocusedInputIdx(null);
                        setTimeout(() => {
                          const active = document.activeElement;
                          const isKeyboardKey = active && (
                            active.classList.contains('keyboard-key') ||
                            active.closest('.on-screen-keyboard-overlay')
                          );
                          const isSameLang = rightInputRefs.current.some((input, i) =>
                            input === active && getKeyboardLangCode(DEMO_MODULE.to) === keyboardArmedLang
                          );
                          if (!isKeyboardKey && !isSameLang) {
                            setKeyboardActiveIdx(null);
                          }
                        }, 100);
                      }}
                    />
                    {focusedInputIdx === index && (
                      <div className="keyboard-icon-absolute" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: isTargetRTL ? 'auto' : '0.3rem', left: isTargetRTL ? '0.3rem' : 'auto', zIndex: 2 }}>
                        <button
                          type="button"
                          className="btn-toggle-keyboard subtle-toggle"
                          tabIndex={-1}
                          style={{
                            marginBottom: '0.25rem',
                            fontSize: '1.1rem',
                            background: 'none',
                            border: 'none',
                            color: '#888',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '1.8rem',
                            width: '1.8rem',
                          }}
                          aria-label={t('showKeyboard')}
                          onMouseDown={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            const langCode = getKeyboardLangCode(DEMO_MODULE.to);
                            if (keyboardArmedLang === langCode) {
                              setKeyboardArmedLang(null);
                              setKeyboardActiveIdx(null);
                            } else {
                              setKeyboardArmedLang(langCode);
                              setKeyboardActiveIdx(index);
                              rightInputRefs.current[index]?.focus();
                            }
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="5" width="18" height="14" rx="2"/>
                            <line x1="7" y1="15" x2="7" y2="15"/>
                            <line x1="11" y1="15" x2="11" y2="15"/>
                            <line x1="15" y1="15" x2="15" y2="15"/>
                            <line x1="7" y1="11" x2="7" y2="11"/>
                            <line x1="11" y1="11" x2="11" y2="11"/>
                            <line x1="15" y1="11" x2="15" y2="11"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  {pairs.length > 1 || (pairs.length === 1 && (pair.left || pair.right)) ? (
                    <button type="button" onClick={() => handleRemovePair(index)} className="btn-remove-pair" aria-label={t('removeWordPair')}>
                      &times;
                    </button>
                  ) : null}
                </div>
              ))}
              <button type="button" onClick={handleAddPair} className="btn-add-pair" aria-label={t('addAnotherWord')}>
                {t('addAnotherWord')}
              </button>
            </div>
            <button onClick={handleManualAdd} className="btn btn-primary btn-confirm-add">{t('addWords')}</button>
          </div>
        )}
        {activeTab === 'ai' && (
          <div className="ai-add-section" style={{ maxWidth: 420, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '2.5rem 2rem', textAlign: 'center' }}>
            <p>{t('letAIGenerateWords') || 'Let AI generate new words for you.'}</p>
            <p className="ai-option-label" style={{ marginBottom: 12 }}>{t('numberOfWordsToGenerate') || 'Number of words to generate:'}</p>
            <div className="ai-word-count-selector" style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24 }}>
              {[5, 10, 25, 50, 100].map(count => (
                <button
                  key={count}
                  className={`btn-count${aiWordCount === count ? ' active' : ''}`}
                  style={{
                    padding: '0.5rem 1.2rem',
                    borderRadius: 8,
                    border: aiWordCount === count ? '2px solid #007bff' : '1px solid #ccc',
                    background: aiWordCount === count ? '#eaf4ff' : '#f8f9fa',
                    color: '#222',
                    fontWeight: 500,
                    fontSize: '1.08rem',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.18s',
                  }}
                  onClick={() => setAiWordCount(count)}
                  type="button"
                >
                  {count}
                </button>
              ))}
            </div>
            <button className="btn btn-primary" style={{ minWidth: 180 }} onClick={() => setShowAICreateMsg(true)}>{t('generateWithAI') || 'Generate with AI'}</button>
            {showAICreateMsg && (
              <div style={{ color: '#b00', marginTop: '1.2rem', fontSize: '1.08rem', fontWeight: 500 }}>
                {t('mustCreateAccountForAI') || 'You must create an account to use this feature.'}
              </div>
            )}
          </div>
        )}
        {keyboardArmedLang && keyboardActiveIdx !== null && (
          <div>
            <OnScreenKeyboard
              languageCode={keyboardArmedLang}
              onInsert={char => {
                const input = rightInputRefs.current[keyboardActiveIdx];
                if (input) {
                  const start = input.selectionStart;
                  const end = input.selectionEnd;
                  const currentValue = input.value;
                  let newValue;
                  if (char === 'backspace') {
                    if (start === end) {
                      newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
                      input.setSelectionRange(start - 1, start - 1);
                    } else {
                      newValue = currentValue.slice(0, start) + currentValue.slice(end);
                      input.setSelectionRange(start, start);
                    }
                  } else if (char === 'space') {
                    newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
                    input.setSelectionRange(start + 1, start + 1);
                  } else {
                    newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
                    input.setSelectionRange(start + char.length, start + char.length);
                  }
                  handlePairChange(keyboardActiveIdx, 'right', newValue);
                  input.focus();
                }
              }}
              onClose={() => {
                setKeyboardArmedLang(null);
                setKeyboardActiveIdx(null);
              }}
            />
          </div>
        )}
      </div>
    );
  }

  const mod = DEMO_MODULE;
  const isTargetRTL = isRTLanguage(getKeyboardLangCode(mod.to));
  const isSourceRTL = isRTLanguage(getKeyboardLangCode(mod.from));

  return (
    <div className="word-group-page-container">
      <div style={{
        background: '#fffbe6',
        border: '1px solid #ffe58f',
        borderRadius: 8,
        padding: '1.2rem 1.5rem',
        margin: '2rem auto 2.5rem auto',
        maxWidth: 700,
        fontSize: '1.08rem',
        color: '#856404',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
        <strong>{t('demoNotice')}</strong> {t('demoNoticeDescription')}<br /><br />
        {t('demoWordsGenerated')}
      </div>
      {/* Top Banner */}
      <div className="word-group-banner">
        <div className="banner-content">
          <button onClick={() => navigate('/')} className="btn btn-secondary btn-back">
            {isRTL ? '→' : '←'} {t('backToModules')}
          </button>
          <h2 className="banner-title">{group.name}</h2>
          <div className="banner-actions">
            <button onClick={() => navigate('/module/demo/group/0/flashcard-mode')} className="btn btn-primary">
              {t('startFlashcards')}
            </button>
            <button onClick={() => navigate('/module/demo/group/0/mcq-practice')} className="btn btn-primary">
              {t('practiceMultipleChoice')}
            </button>
            <button onClick={() => navigate('/module/demo/group/0/practice')} className="btn btn-primary">
              {t('practiceSentences')}
            </button>
            <button onClick={() => navigate('/module/demo/group/0/story')} className="btn btn-primary">
              {t('readAStory')}
            </button>
          </div>
        </div>
      </div>
      {/* Edit Controls */}
      <div className="edit-controls" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        {!isEditing && (
          <>
            <button onClick={handleEditWords} className="btn btn-secondary">
              {t('editWords')}
            </button>
            <button onClick={() => setShowAddWords(v => !v)} className="btn btn-secondary">
              {t('addMoreWords')}
            </button>
          </>
        )}
        {isEditing && (
          <div className="edit-actions">
            <button onClick={handleSaveWords} className="btn btn-primary">
              {t('saveChanges')}
            </button>
            <button onClick={handleCancelEdit} className="btn btn-secondary">
              {t('cancel')}
            </button>
          </div>
        )}
      </div>
      {/* Add More Words panel below the buttons */}
      {showAddWords && !isEditing && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '2.5rem' }}>
          <AddMoreWordsPanel onWordsAdded={handleWordsAdded} />
        </div>
      )}
      <div className="word-group-actions" style={{ margin: '32px 0 0 0' }}>
        {/* Google Ad above the edit/add buttons */}
        {/* ... existing code for edit/add buttons ... */}
      </div>
      <div className="card word-list-card">
        {/* Column Headers */}
        <div className="word-list-header">
          <span className="word-header-from">{t(mod.from)}</span>
          <span className="word-header-to" style={isRTL ? { textAlign: 'left' } : {}}>
            {t(mod.to)}
          </span>
        </div>
        {(isEditing ? editedWords : group.words).map((word, idx) => {
          return (
            <div key={idx} className="word-list-row">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={word[mod.from]}
                    onChange={e => handleWordChange(idx, mod.from, e.target.value)}
                    className="word-input word-from-input"
                    placeholder={`${t(mod.from)} word`}
                    style={{
                      direction: isSourceRTL ? 'rtl' : 'ltr',
                      textAlign: isSourceRTL ? 'right' : 'left',
                    }}
                  />
                  <div className="word-edit-controls" style={{ width: '100%' }}>
                    <div className="input-with-keyboard-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <input
                        type="text"
                        value={word[mod.to]}
                        onChange={e => handleWordChange(idx, mod.to, e.target.value)}
                        className="word-input word-to-input"
                        placeholder={`${t(mod.to)} word`}
                        style={{
                          direction: isTargetRTL ? 'rtl' : 'ltr',
                          textAlign: isTargetRTL ? 'right' : 'left',
                          paddingRight: !isTargetRTL ? '2.2rem' : undefined,
                          paddingLeft: isTargetRTL ? '2.2rem' : undefined,
                          marginLeft: 'auto',
                          width: '100%',
                          maxWidth: 320,
                        }}
                        ref={el => wordToInputRefs.current[idx] = el}
                        onFocus={() => {
                          setFocusedInputIdx(idx);
                          setFocusedLang(getKeyboardLangCode(mod.to));
                          if (keyboardArmedLang === getKeyboardLangCode(mod.to)) {
                            setKeyboardActiveIdx(idx);
                          }
                        }}
                        onBlur={e => {
                          setFocusedInputIdx(null);
                          setFocusedLang(null);
                          setTimeout(() => {
                            const active = document.activeElement;
                            const isKeyboardKey = active && (
                              active.classList.contains('keyboard-key') ||
                              active.closest('.on-screen-keyboard-overlay')
                            );
                            const isSameLang = wordToInputRefs.current.some((input, i) =>
                              input === active && getKeyboardLangCode(mod.to) === keyboardArmedLang
                            );
                            if (!isKeyboardKey && !isSameLang) {
                              setKeyboardActiveIdx(null);
                            }
                          }, 100);
                        }}
                      />
                      {focusedInputIdx === idx && (
                        <div className="keyboard-icon-absolute" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: isTargetRTL ? 'auto' : '0.3rem', left: isTargetRTL ? '0.3rem' : 'auto', zIndex: 2 }}>
                          <button
                            type="button"
                            className="btn-toggle-keyboard subtle-toggle"
                            tabIndex={-1}
                            style={{
                              marginBottom: '0.25rem',
                              fontSize: '1.1rem',
                              background: 'none',
                              border: 'none',
                              color: '#888',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'background 0.2s',
                              lineHeight: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '1.8rem',
                              width: '1.8rem',
                            }}
                            aria-label={t('showKeyboard')}
                            onMouseDown={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              const langCode = getKeyboardLangCode(mod.to);
                              if (keyboardArmedLang === langCode) {
                                setKeyboardArmedLang(null);
                                setKeyboardActiveIdx(null);
                              } else {
                                setKeyboardArmedLang(langCode);
                                setKeyboardActiveIdx(idx);
                                wordToInputRefs.current[idx]?.focus();
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="5" width="18" height="14" rx="2"/>
                              <line x1="7" y1="15" x2="7" y2="15"/>
                              <line x1="11" y1="15" x2="11" y2="15"/>
                              <line x1="15" y1="15" x2="15" y2="15"/>
                              <line x1="7" y1="11" x2="7" y2="11"/>
                              <line x1="11" y1="11" x2="11" y2="11"/>
                              <line x1="15" y1="11" x2="15" y2="11"/>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteWord(idx)}
                      className="btn-delete-word"
                      title={t('deleteWord')}
                      aria-label={t('deleteWord')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="word-from">{word[mod.from]}</span>
                  <span
                    className="word-to"
                    style={{
                      direction: isTargetRTL ? 'rtl' : 'ltr',
                      textAlign: isTargetRTL ? 'right' : 'left',
                    }}
                  >
                    {word[mod.to]}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
      {keyboardArmedLang && keyboardActiveIdx !== null && (
        <div ref={keyboardOverlayRef}>
          <OnScreenKeyboard
            languageCode={keyboardArmedLang}
            onInsert={char => {
              const input = wordToInputRefs.current[keyboardActiveIdx];
              if (input) {
                const start = input.selectionStart;
                const end = input.selectionEnd;
                const currentValue = input.value;
                let newValue;
                if (char === 'backspace') {
                  if (start === end) {
                    newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
                    input.setSelectionRange(start - 1, start - 1);
                  } else {
                    newValue = currentValue.slice(0, start) + currentValue.slice(end);
                    input.setSelectionRange(start, start);
                  }
                } else if (char === 'space') {
                  newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
                  input.setSelectionRange(start + 1, start + 1);
                } else {
                  newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
                  input.setSelectionRange(start + char.length, start + char.length);
                }
                handleWordChange(keyboardActiveIdx, mod.to, newValue);
                input.focus();
              }
            }}
            onClose={() => {
              setKeyboardArmedLang(null);
              setKeyboardActiveIdx(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default DemoWordGroupPage; 